import { ZuruflexClient, } from './client.js';
/**
 * Holds a signed-in session for one browser tab.
 *
 * The access token lives in memory only. The refresh token is persisted,
 * because the alternative is signing someone out every time they reload —
 * and it is the credential that can be revoked from the server, which the
 * access token deliberately cannot.
 *
 * Every tab of the same app shares that one stored refresh token, and the
 * server reads a token presented twice as theft and revokes every session
 * the person has. So a tab never trusts its own copy: it follows other
 * tabs' writes as they happen, and it rotates the token under a lock that
 * spans tabs, re-reading storage once it holds it. Without both, two tabs
 * left open signed everybody out each time the access token ran out.
 */
export class ZuruflexSession {
    client;
    options;
    storageKey;
    accessToken;
    refreshToken;
    session = null;
    /** In flight refresh, so a burst of 401s makes one exchange and not six. */
    refreshing;
    /**
     * Set while the exchange request itself is on the wire.
     *
     * Coalescing onto `refreshing` is right for the ordinary caller — six
     * requests meeting the same expired token should make one exchange — but
     * fatal for a 401 raised *by* the exchange, which would be handed the
     * promise it is itself blocking. That caller is told no instead.
     */
    exchanging = false;
    listeners = new Set();
    constructor(options) {
        this.options = options;
        this.storageKey = options.storageKey ?? `zuruflex.session.${options.realm.toLowerCase()}`;
        this.refreshToken = this.readStored()?.refreshToken;
        this.followOtherTabs();
        this.client = new ZuruflexClient({
            ...options,
            token: () => this.accessToken,
            onUnauthorized: () => this.refresh(),
        });
    }
    get current() {
        return this.session;
    }
    /**
     * Watch the session. A refresh that fails signs the user out from deep
     * inside a background request, so a UI cannot rely on only hearing about
     * changes it initiated itself.
     */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    emit() {
        this.options.onChange?.(this.session);
        for (const listener of this.listeners)
            listener(this.session);
    }
    get isSignedIn() {
        return this.session !== null;
    }
    /** True when a reload left us a refresh token worth trying. */
    get canRestore() {
        return Boolean(this.refreshToken) && this.session === null;
    }
    /**
     * Called once on boot. Trades the stored refresh token for a session, or
     * clears it if the server has since revoked it.
     *
     * An exchange already in flight is awaited rather than skipped. Calling
     * `refresh` here would meet the `exchanging` guard below — which exists
     * for a caller that must *not* wait — and return immediately with the
     * session still null. A gated route reading that as "not signed in"
     * bounced the visitor to sign-in while their own restore was still on
     * the wire, which is exactly what React's development double-mount
     * provokes on every single page load.
     */
    async restore() {
        if (!this.refreshToken)
            return null;
        await (this.refreshing ?? this.refresh());
        return this.session;
    }
    adopt(session) {
        this.accessToken = session.accessToken;
        this.refreshToken = session.refreshToken;
        this.session = session;
        this.writeStored({ realm: this.options.realm, refreshToken: session.refreshToken });
        this.emit();
        return session;
    }
    async signInWithPassword(email, password) {
        const { realm } = this.options;
        const session = realm === 'STAFF'
            ? await this.client.staffSignIn(email, password)
            : realm === 'CUSTOMER'
                ? await this.client.customerSignIn(email, password)
                : await this.client.partnerSignIn(email, password);
        return this.adopt(session);
    }
    /**
     * `signUp` means two different things to the two realms that accept it,
     * which is why it is one loose bag rather than a typed parameter: a
     * partner sends the whole business, and a customer sends `create` and a
     * locale. Neither is anything to a staff session, which cannot be
     * self-served at all.
     */
    async signInWithGoogle(credential, signUp) {
        const { realm } = this.options;
        const session = realm === 'STAFF'
            ? await this.client.staffGoogleSignIn(credential)
            : realm === 'CUSTOMER'
                ? await this.client.customerGoogleSignIn(credential, signUp ?? {})
                : await this.client.partnerGoogleSignIn(credential, signUp);
        return this.adopt(session);
    }
    /**
     * Sign in with Apple. Phones only, and never staff: the operations
     * dashboard is a browser app with no Apple sign-in to offer.
     */
    async signInWithApple(credential, signUp) {
        const { realm } = this.options;
        if (realm === 'STAFF')
            throw new Error('Sign in with Apple is not offered to staff');
        const session = realm === 'CUSTOMER'
            ? await this.client.customerAppleSignIn(credential, signUp ?? {})
            : await this.client.partnerAppleSignIn(credential, signUp);
        return this.adopt(session);
    }
    /**
     * Spends a WhatsApp sign-in code and adopts the session behind it.
     *
     * Customers only: a phone number is not how a partner or a member of
     * staff gets in, and there is no route on the API for either.
     */
    async signInWithPhone(phone, code, locale) {
        if (this.options.realm !== 'CUSTOMER') {
            throw new Error('Phone sign-in is for customers only');
        }
        return this.adopt(await this.client.verifyPhoneSignIn(phone, code, locale));
    }
    async signUp(input) {
        const session = this.options.realm === 'CUSTOMER'
            ? await this.client.customerSignUp(input)
            : await this.client.partnerSignUp(input);
        return this.adopt(session);
    }
    /**
     * Spends a reset link and adopts the session it returns, so the person
     * who has just chosen a password is signed in rather than shown the form
     * they came from.
     */
    async resetPassword(token, newPassword) {
        return this.adopt(await this.client.resetPassword(token, newPassword));
    }
    /**
     * Tells the server to revoke the refresh token, then forgets everything
     * locally whether or not that call succeeded — a failed network request
     * must not leave somebody apparently still signed in.
     */
    async signOut() {
        const token = this.refreshToken;
        this.clear();
        if (token)
            await this.client.signOut(token).catch(() => undefined);
    }
    async refresh() {
        if (this.exchanging)
            return undefined;
        if (this.refreshing)
            return this.refreshing;
        this.refreshing = (async () => {
            if (!this.refreshToken)
                return undefined;
            this.exchanging = true;
            try {
                return await this.acrossTabs(() => this.exchange());
            }
            finally {
                this.exchanging = false;
                this.refreshing = undefined;
            }
        })();
        return this.refreshing;
    }
    /**
     * The exchange itself, run while holding the cross-tab lock.
     *
     * The token is read back from storage rather than taken from memory: if
     * another tab rotated it while this one waited for the lock, the copy in
     * memory has already been spent, and presenting it would revoke both.
     */
    async exchange() {
        const token = this.sharedAcrossTabs ? this.readStored()?.refreshToken : this.refreshToken;
        if (!token) {
            this.clear();
            return undefined;
        }
        try {
            const session = await this.client.refreshSession(token);
            this.adopt(session);
            return session.accessToken;
        }
        catch {
            // Expired, revoked, or replayed. Either way this session is over.
            this.clear();
            return undefined;
        }
    }
    /**
     * Runs `work` while no other tab of this app is refreshing. Where there
     * are no Web Locks — a phone, a test, an old browser — there are no
     * other tabs to race either, so it simply runs.
     */
    acrossTabs(work) {
        const locks = globalThis.navigator?.locks;
        if (!locks || !this.sharedAcrossTabs)
            return work();
        return locks.request(`${this.storageKey}:refresh`, work);
    }
    /**
     * Keeps this tab's refresh token in step with the others. A new token
     * written elsewhere replaces ours — the access token in memory is still
     * good until it expires, so it is kept. A removed one means that tab
     * signed out or found the session revoked, and this one follows.
     *
     * The browser only fires `storage` events in *other* tabs, so this never
     * hears its own writes.
     */
    followOtherTabs() {
        if (!this.sharedAcrossTabs)
            return;
        const target = globalThis;
        if (typeof target.addEventListener !== 'function')
            return;
        target.addEventListener('storage', (event) => {
            if (event.key !== this.storageKey)
                return;
            this.onStoredChange(event.newValue);
        });
    }
    onStoredChange(raw) {
        if (raw === null) {
            if (this.refreshToken || this.session)
                this.clear();
            return;
        }
        try {
            const parsed = JSON.parse(raw);
            if (parsed.realm === this.options.realm)
                this.refreshToken = parsed.refreshToken;
        }
        catch {
            // Not ours to understand; leave this tab as it is.
        }
    }
    clear() {
        this.accessToken = undefined;
        this.refreshToken = undefined;
        this.session = null;
        this.storage?.removeItem(this.storageKey);
        this.emit();
    }
    /**
     * True only for the default `localStorage` in a browser — the one store
     * other tabs can write to. A storage passed in (a phone's keychain, a
     * test's map) belongs to this session alone.
     */
    get sharedAcrossTabs() {
        return this.options.storage === undefined && this.storage !== null;
    }
    get storage() {
        if (this.options.storage === null)
            return null;
        if (this.options.storage)
            return this.options.storage;
        // Private browsing and blocked site data both throw on access, not on
        // use, so this is guarded rather than assumed.
        try {
            return globalThis.localStorage ?? null;
        }
        catch {
            return null;
        }
    }
    readStored() {
        try {
            const raw = this.storage?.getItem(this.storageKey);
            if (!raw)
                return null;
            const parsed = JSON.parse(raw);
            // A staff token in the partner portal's slot is not usable there.
            return parsed.realm === this.options.realm ? parsed : null;
        }
        catch {
            return null;
        }
    }
    writeStored(value) {
        try {
            this.storage?.setItem(this.storageKey, JSON.stringify(value));
        }
        catch {
            // Out of quota, or storage disabled. The session still works for
            // this page view; it just will not survive a reload.
        }
    }
}
